import { styled } from '@linaria/react';
import { Flex, Group } from '@mantine/core';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type {
  ModelComparisonFamily,
  ModelComparisonResponse,
} from '../../../api/agent-miner-client/agent-miner-client.type';
import { ChartBox } from '../../../components/chart-styles/chart-styles';
import { inkLight } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { H3, Text } from '../../../ui-library/typography/typography';
import { StyledButton } from '../../financials/charts/shared/chart-display-type-button-set';
import { useModelComparisonHiddenFamilies } from '../model-comparison/model-comparison.hooks';
import type { SessionsDrilldownWindow } from '../model-comparison/model-comparison.type';
import { UsageChart } from './usage-chart';
import { UsageTable } from './usage-table';
import { useUsageMeasure, useUsageView } from './usage.hooks';
import type { UsageMeasure } from './usage.type';

/** Measure options rendered in the sidebar selector, in display order. */
const USAGE_MEASURES: UsageMeasure[] = ['total', 'input', 'output', 'cache_read', 'cache_write'];

/**
 * Resolves the translated label for a token measure, shared by the sidebar
 * selector and the chart's Y-axis. Keys are mapped explicitly (not built via
 * template interpolation) so they satisfy the statically-typed i18n key union.
 *
 * @param {UsageMeasure} measure - The measure to label
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {string} The measure's display label
 */
const measureLabel = (measure: UsageMeasure, t: TFunction): string => {
  switch (measure) {
    case 'input':
      return t('agent_miner.usage.measure_input');
    case 'output':
      return t('agent_miner.usage.measure_output');
    case 'cache_read':
      return t('agent_miner.usage.measure_cache_read');
    case 'cache_write':
      return t('agent_miner.usage.measure_cache_write');
    default:
      return t('agent_miner.usage.measure_total');
  }
};

type UsageViewProps = {
  /** The model comparison payload, already narrowed by the provider filter. */
  response: ModelComparisonResponse;
  /**
   * The full, provider-filter-unaware family list, used only to seed stable
   * chart colors.
   */
  allFamilies: ModelComparisonFamily[];
  /**
   * Opens the sessions side panel for a clicked chart point's model family and
   * period. Applies to both the line and bar variants.
   */
  onSelectFamily?: (modelFamily: string, window?: SessionsDrilldownWindow) => void;
};

/**
 * The Token-usage view of the Model Comparison tab: a header carrying the
 * title and a Chart/Table toggle on the right, above either the tokens-per-
 * model chart or the per-model token-breakdown table. The chart/table choice
 * lives in the header (the `usage_view` URL param) rather than the sidebar, so
 * it is local to this view and doesn't disturb the cycle-time chart/table
 * selector.
 *
 * @param {UsageViewProps} props - Component props
 * @returns {JSX.Element} The token-usage chart or table with its header toggle
 */
export const UsageView = ({ response, allFamilies, onSelectFamily }: UsageViewProps) => {
  const { t } = useTranslation();
  const { measure } = useUsageMeasure();
  const { usageView, setUsageView } = useUsageView();
  const { hiddenFamilies, toggleFamily } = useModelComparisonHiddenFamilies();

  // A missing token series while rows are present is the FE-deployed-before-BE
  // case (`token_usage_series` is optional at the wire boundary). Only the chart
  // sub-views depend on it — the table reads per-row token totals — so gate the
  // chart here rather than the whole view, and keep the header (with the
  // chart/table toggle) visible so the user can still switch to the table.
  const hasSeries = (response.token_usage_series?.length ?? 0) > 0;

  return (
    <Flex direction="column" gap={12} h="100%" data-testid="usage-view">
      <Header>
        <TitleBlock>
          <H3>{t('agent_miner.usage.chart_title')}</H3>
          <Text size="small" color={inkLight}>
            {t('agent_miner.usage.chart_subtitle')}
          </Text>
        </TitleBlock>
        <Group gap="xs" data-testid="usage-view-toggle">
          <StyledButton
            label={t('agent_miner.usage.toggle_line')}
            iconName="stacked_line_chart"
            selected={usageView === 'line'}
            onClick={() => setUsageView('line')}
          />
          <StyledButton
            label={t('agent_miner.usage.toggle_bar')}
            iconName="bar_chart"
            selected={usageView === 'bar'}
            onClick={() => setUsageView('bar')}
          />
          <StyledButton
            label={t('agent_miner.usage.toggle_table')}
            iconName="table_chart"
            selected={usageView === 'table'}
            onClick={() => setUsageView('table')}
          />
        </Group>
      </Header>
      {usageView === 'table' ? (
        <UsageTable rows={response.rows} />
      ) : hasSeries ? (
        <ChartBox style={{ flex: 1, minHeight: 0 }}>
          <UsageChart
            response={response}
            allFamilies={allFamilies}
            measure={measure}
            variant={usageView === 'bar' ? 'bar' : 'line'}
            axisLabel={measureLabel(measure, t)}
            hiddenFamilies={hiddenFamilies}
            onToggleFamily={toggleFamily}
            onSelectFamily={onSelectFamily}
            embedded
          />
        </ChartBox>
      ) : (
        <EmptyChart>
          <Icon name="insights" color={inkLight} size={48} />
          <Text size="regular" color={inkLight}>
            {t('agent_miner.usage.empty')}
          </Text>
        </EmptyChart>
      )}
    </Flex>
  );
};

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

/* Fills the chart area with a centered empty state when the token series is
   absent, matching the height the chart would have occupied. */
const EmptyChart = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 280px;
`;

export { USAGE_MEASURES, measureLabel };
